import { z } from 'zod';
import { catalogueStatusSchema, hvacStateSchema, routeTypeSchema, seasonSchema, testTypeSchema, timeBandSchema, trafficLevelSchema, } from '../../common/schemas/enum.schemas.js';
import { idParamSchema, uuidSchema } from '../../common/schemas/id.schemas.js';
import { paginatedSchema, paginationInputSchema, sortInputSchema } from '../../common/schemas/pagination.schemas.js';
export const MIN_IDLE_DURATION_MIN = 1;
export const MAX_IDLE_DURATION_MIN = 480;
export const scenarioSchema = z.object({
    id: uuidSchema,
    protocolId: uuidSchema,
    protocolCode: z.string().min(1),
    protocolVersion: z.number().int().min(1),
    protocolUnit: z.string().min(1),
    code: z.string().min(1),
    testType: testTypeSchema,
    season: seasonSchema,
    timeBand: timeBandSchema,
    hvacState: hvacStateSchema,
    routeType: routeTypeSchema.nullable(),
    idleDurationMin: z.number().int().nullable(),
    /** The traffic the scenario is defined for; `null` means it does not prescribe one. */
    trafficLevel: trafficLevelSchema.nullable(),
    isCore: z.boolean(),
    status: catalogueStatusSchema,
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
export const SCENARIO_SORT_FIELDS = ['code', 'testType', 'season', 'createdAt'];
export const scenarioSortBySchema = z.enum(SCENARIO_SORT_FIELDS);
export const listScenariosInputSchema = z.object({
    ...paginationInputSchema.shape,
    ...sortInputSchema.shape,
    sortBy: scenarioSortBySchema.default('code'),
    search: z.string().trim().min(1).optional(),
    protocolId: uuidSchema.optional(),
    testType: testTypeSchema.optional(),
    season: seasonSchema.optional(),
    // Left as a plain boolean on purpose: `z.coerce.boolean()` would read the query string "false" as
    // `true`. `ZodSmartCoercionPlugin` converts the query string correctly before zod sees it.
    isCore: z.boolean().optional(),
    status: catalogueStatusSchema.optional(),
});
export const listScenariosOutputSchema = paginatedSchema(scenarioSchema);
/**
 * The dimension tuple is what makes a scenario unique (plan §3.3 two-layer rule). A DRIVE scenario
 * carries a route type and no idle duration; an IDLE scenario carries a duration and no route. Traffic is
 * optional on either count of the tuple's uniqueness — an omitted traffic level is its own distinct
 * scenario ("traffic unspecified"), not a wildcard.
 */
const scenarioDimensionsShape = {
    protocolId: uuidSchema,
    testType: testTypeSchema,
    season: seasonSchema,
    timeBand: timeBandSchema,
    hvacState: hvacStateSchema,
    routeType: routeTypeSchema.optional(),
    idleDurationMin: z.coerce.number().int().min(MIN_IDLE_DURATION_MIN).max(MAX_IDLE_DURATION_MIN).optional(),
    /** The *expected* traffic. DRIVE only — an idling car is not in traffic. */
    trafficLevel: trafficLevelSchema.optional(),
};
/** Shared by create and update so the two can never drift apart. */
const checkTestTypeCoherence = (value, ctx) => {
    if (value.testType === 'DRIVE') {
        if (value.routeType === undefined) {
            ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['routeType'], message: 'A drive scenario needs a route type' });
        }
        if (value.idleDurationMin !== undefined) {
            ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['idleDurationMin'], message: 'A drive scenario has no idle duration' });
        }
        return;
    }
    if (value.idleDurationMin === undefined) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['idleDurationMin'], message: 'An idle scenario needs a duration in minutes' });
    }
    if (value.routeType !== undefined) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['routeType'], message: 'An idle scenario has no route' });
    }
    if (value.trafficLevel !== undefined) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['trafficLevel'],
            message: 'An idle scenario has no traffic level — a stationary car is not in traffic',
        });
    }
};
export const createScenarioInputSchema = z
    .object({
    ...scenarioDimensionsShape,
    code: z
        .string()
        .trim()
        .min(1, 'Code is required')
        .max(64)
        .regex(/^[A-Za-z0-9][A-Za-z0-9-]*$/, 'Use letters, digits and hyphens only'),
    isCore: z.boolean().default(false),
})
    .superRefine(checkTestTypeCoherence);
export const updateScenarioInputSchema = z
    .object({
    ...idParamSchema.shape,
    ...scenarioDimensionsShape,
    isCore: z.boolean().default(false),
    status: catalogueStatusSchema.optional(),
})
    .superRefine(checkTestTypeCoherence);
//# sourceMappingURL=scenarios.schemas.js.map